interface SmartDocsFieldForSorting {
  importance?: string
  processing?: boolean
}

interface SmartDocFieldWithStatus {
  status?: string
}

interface SmartDocBlockForCounting {
  fields?: SmartDocFieldWithStatus[] | null
}

export const normalizeSmartDocsFields = <T>(fields?: T[] | null): T[] =>
  Array.isArray(fields) ? fields : []

export const normalizeSmartDocsAnalysisBlocks = <
  TField = unknown,
  T extends { fields?: TField[] | null } = { fields?: TField[] | null },
>(
  docAnalysis?: T | T[] | null,
): Array<Omit<T, 'fields'> & { fields: TField[] }> => {
  const blocks = Array.isArray(docAnalysis)
    ? docAnalysis
    : docAnalysis
    ? [docAnalysis]
    : []

  return blocks.map((block) => ({
    ...block,
    fields: normalizeSmartDocsFields(block.fields),
  }))
}

export const getTotalAndCheckItemNumber = (
  allData: SmartDocBlockForCounting[],
) => {
  let totalDocNumber = 0
  let docNumberToCheck = 0
  allData.forEach((item) => {
    normalizeSmartDocsFields(item.fields).forEach((field) => {
      totalDocNumber++
      if (field.status === 'unverified') {
        docNumberToCheck++
      }
    })
  })
  return { totalDocNumber, docNumberToCheck }
}

const importanceRank: Record<string, number> = {
  high: 0,
  medium: 1,
  low: 2,
}

const getImportanceRank = (importance?: string) =>
  importance ? importanceRank[importance] ?? 3 : 3

export const sortSmartDocsFieldsForPM = <T extends SmartDocsFieldForSorting>(
  fields?: T[] | null,
): T[] =>
  normalizeSmartDocsFields(fields)
    .map((field, index) => ({ field, index }))
    .sort((a, b) => {
      const aImportance = getImportanceRank(a.field.importance)
      const bImportance = getImportanceRank(b.field.importance)
      if (aImportance !== bImportance) {
        return aImportance - bImportance
      }

      if (Boolean(a.field.processing) !== Boolean(b.field.processing)) {
        return a.field.processing ? -1 : 1
      }

      return a.index - b.index
    })
    .map(({ field }) => field)
